import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  DataGridPremium,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_TREE_DATA_GROUPING_FIELD,
  useGridApiRef
} from '@mui/x-data-grid-premium';
import { isEmpty, isEqual } from 'lodash/lang';

import { useEuiFiltersState, withEuiFiltersProvider } from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import { FRS_READY } from 'components/eui/EuiFiltersContext/reducer';
import {
  CHILD_ROW,
  COLUMNS_HIDDEN_BY_DEFAULT,
  PARENT_ROW,
  PATIENT_DATABASE_TABLE_NAME,
  PATIENT_DATABASE_TABLE_VERSION,
  WEBFORM_SOURCE_NAME
} from 'components/pages/pre-screen/PatientDatabase/constants';

import { PatientDatabaseApi } from '../../../../api';
import NotificationManager from '../../../../common/notifications/NotificationManager';
import {
  MANAGE_PATIENT_LEAD_STATUS,
  PUSH_PATIENT_DATABASE_TO_PRE_SCREENING
} from '../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../services/auth';
import { onRequestError } from '../../../../services/handlers';
import { muiDataGridLocaleText } from '../../../../services/i18n/mui';

import FiltersSection from './FiltersSection/FiltersSection';
import { COLUMNS } from './columns';
import { mapToTreeColumns } from './PatientDatabaseColumnsModifierService';
import { PatientDatabaseFooter } from './PatientDatabaseFooter';
import { getPatientTreePath, groupRows } from './PatientDatabaseGroupingService';
import { resolveNextRowSelectionModel } from './PatientDatabaseSelectionService';
import { hasActiveFilters, toRequestFilters } from './PatientDatabaseService';
import { PatientDatabaseToolbar } from './PatientDatabaseToolbar';
import { usePushToPreScreening } from './usePushToPreScreening';

import './PatientDatabase.scss';

const CHECKBOX_COLUMN = GRID_CHECKBOX_SELECTION_COL_DEF.field;
const PINNED_IDENTITY_COLUMNS = [GRID_TREE_DATA_GROUPING_FIELD, CHECKBOX_COLUMN];
const NON_TOGGLABLE_COLUMNS = new Set(PINNED_IDENTITY_COLUMNS);

const COLUMN_GROUPING_MODEL = [];

const INITIAL_FILTER_MODEL = {
  items: [],
  quickFilterExcludeHiddenColumns: false
};

const TOOLBAR_EXPORT_OPTIONS = {
  printOptions: { disableToolbarButton: true },
  excelOptions: { disableToolbarButton: true },
  csvOptions: { utf8WithBom: true, escapeFormulas: false }
};

const getTogglableColumns = columns =>
  columns.filter(column => !NON_TOGGLABLE_COLUMNS.has(column.field)).map(column => column.field);

const PatientDatabase = () => {
  const previousRequestObject = useRef(null);
  const canModifyPatients = userHasAccessTo(PUSH_PATIENT_DATABASE_TO_PRE_SCREENING);
  const canEditLeadStatus = userHasAccessTo(MANAGE_PATIENT_LEAD_STATUS);

  const [patients, setPatients] = useState([]);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [alreadyLoaded, setAlreadyLoaded] = useState(false);
  const [failedRowIds, setFailedRowIds] = useState(new Set());
  const [rowSelectionModel, setRowSelectionModel] = useState({
    type: 'include',
    ids: new Set()
  });
  const [pinnedColumns, setPinnedColumns] = useState({
    left: PINNED_IDENTITY_COLUMNS,
    right: []
  });

  const apiRef = useGridApiRef();

  const { schema, ssu, therapeuticArea, indication } = useEuiFiltersState();

  const requestObject = useMemo(
    function() {
      if (schema?.status !== FRS_READY || isEmpty(ssu)) {
        return;
      }
      const nextRequestObject = toRequestFilters(ssu, therapeuticArea, indication);

      if (isEqual(previousRequestObject.current, nextRequestObject)) {
        return previousRequestObject.current;
      }

      previousRequestObject.current = nextRequestObject;
      return nextRequestObject;
    },
    [schema, ssu, therapeuticArea, indication]
  );

  const loadTableData = useCallback(function(request) {
    return PatientDatabaseApi.getPatientsByFilters(request).then(({ data }) => {
      setPatients(data);
      setAlreadyLoaded(true);
      setFiltersApplied(true);
    }, onRequestError);
  }, []);

  const onApply = useCallback(
    function() {
      if (requestObject && hasActiveFilters(requestObject)) {
        loadTableData(requestObject);
      }
    },
    [loadTableData, requestObject]
  );

  const reloadTableData = useCallback(() => loadTableData(requestObject), [loadTableData, requestObject]);

  const { onPushToPreScreening } = usePushToPreScreening({
    apiRef,
    onPushSuccess: reloadTableData,
    setFailedRowIds,
    setRowSelectionModel
  });

  const handlePinnedColumnsChange = useCallback(updatedPinnedColumns => {
    setPinnedColumns({
      ...updatedPinnedColumns,
      left: [
        ...PINNED_IDENTITY_COLUMNS,
        ...updatedPinnedColumns.left.filter(column => !PINNED_IDENTITY_COLUMNS.includes(column))
      ]
    });
  }, []);

  const isLeadStatusCellEditable = useCallback(
    ({ row, field }) =>
      field !== 'patientLeadStatus' ||
      (row.patientRowType === CHILD_ROW && row.source === WEBFORM_SOURCE_NAME && canEditLeadStatus),
    [canEditLeadStatus]
  );

  const processRowUpdate = useCallback(async (newRow, oldRow) => {
    if (newRow.patientLeadStatus === oldRow.patientLeadStatus) {
      return oldRow;
    }

    try {
      await PatientDatabaseApi.updateLeadStatus(newRow.id, newRow.patientLeadStatus);
      setPatients(previousGroups =>
        previousGroups.map(group =>
          group.patientId === newRow.patientId
            ? {
                ...group,
                children: (group.children ?? []).map(child =>
                  child.id === newRow.id ? { ...child, patientLeadStatus: newRow.patientLeadStatus } : child
                )
              }
            : group
        )
      );
      return newRow;
    } catch (error) {
      NotificationManager.error('Failed to update Patient Lead Status.');
      return oldRow;
    }
  }, []);

  const columns = useMemo(() => mapToTreeColumns(COLUMNS), []);

  const defaultTableState = useMemo(
    () => ({
      columns: {
        orderedFields: [...PINNED_IDENTITY_COLUMNS, ...columns.map(column => column.field)],
        columnVisibilityModel: COLUMNS_HIDDEN_BY_DEFAULT
      },
      filter: { filterModel: INITIAL_FILTER_MODEL },
      pinnedColumns: { left: PINNED_IDENTITY_COLUMNS, right: [] }
    }),
    [columns]
  );

  const groupedPatients = useMemo(() => groupRows(patients), [patients]);
  const patientGroupCount = useMemo(() => groupedPatients.filter(row => row.patientRowType === PARENT_ROW).length, [
    groupedPatients
  ]);
  const selectablePatientIds = useMemo(
    () => new Set(groupedPatients.filter(row => row.patientRowType === PARENT_ROW).map(row => row.id)),
    [groupedPatients]
  );

  return (
    <div className="patient-database">
      <FiltersSection
        onApply={onApply}
        alreadyLoaded={alreadyLoaded}
        setAlreadyLoaded={setAlreadyLoaded}
        request={requestObject}
      />
      {filtersApplied ? (
        <DataGridPremium
          className="patient-database-table"
          apiRef={apiRef}
          getRowId={row => row.id}
          rows={groupedPatients}
          columns={columns}
          isCellEditable={isLeadStatusCellEditable}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={() => NotificationManager.error('Failed to update Patient Lead Status.')}
          onCellClick={(params, event) => {
            if (params.field === 'patientLeadStatus' && params.isEditable) {
              event.stopPropagation();
              if (apiRef.current.getCellMode(params.id, params.field) === 'view') {
                apiRef.current.startCellEditMode({ id: params.id, field: params.field });
              }
            }
          }}
          treeData
          getTreeDataPath={getPatientTreePath}
          defaultGroupingExpansionDepth={1}
          groupingColDef={{
            headerName: '',
            width: 40,
            minWidth: 40,
            maxWidth: 40,
            disableExport: true,
            hideDescendantCount: true,
            valueFormatter: () => ''
          }}
          rowHeight={38}
          localeText={muiDataGridLocaleText({ noRowsLabel: 'No Record Found' })}
          disableRowSelectionOnClick
          disableRowGrouping
          disableAggregation
          disablePivoting
          showToolbar
          checkboxSelection={canModifyPatients}
          disableRowSelectionExcludeModel
          rowSelectionPropagation={{ descendants: false, parents: false }}
          isRowSelectable={({ row }) => row.patientRowType === PARENT_ROW}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={newRowSelectionModel => {
            const nextSelectionModel = resolveNextRowSelectionModel({
              currentSelectionModel: rowSelectionModel,
              newRowSelectionModel,
              selectablePatientIds
            });

            setRowSelectionModel(nextSelectionModel);
            setFailedRowIds(current => new Set([...current].filter(id => nextSelectionModel.ids.has(id))));
          }}
          getRowClassName={({ row }) =>
            [
              row.patientRowType === CHILD_ROW ? 'patient-database-detail-row' : '',
              failedRowIds.has(row.id) ? 'patient-database-row-push-failed' : ''
            ]
              .filter(Boolean)
              .join(' ')
          }
          pinnedColumns={pinnedColumns}
          onPinnedColumnsChange={handlePinnedColumnsChange}
          initialState={{
            columns: {
              columnVisibilityModel: COLUMNS_HIDDEN_BY_DEFAULT
            },
            filter: {
              filterModel: INITIAL_FILTER_MODEL
            },
            pinnedColumns
          }}
          slots={{
            toolbar: PatientDatabaseToolbar,
            footer: PatientDatabaseFooter
          }}
          slotProps={{
            columnsManagement: {
              getTogglableColumns
            },
            toolbar: {
              ...TOOLBAR_EXPORT_OPTIONS,
              refreshData: reloadTableData,
              defaultTableState,
              columns,
              columnGroupingModel: COLUMN_GROUPING_MODEL,
              tableVersion: PATIENT_DATABASE_TABLE_VERSION,
              tableName: PATIENT_DATABASE_TABLE_NAME,
              ...(canModifyPatients && { onPushToPreScreening })
            },
            footer: {
              totalRowCount: patientGroupCount
            }
          }}
          sx={{ border: 'none', padding: '0 40px' }}
        />
      ) : (
        <div className="patient-database-empty-state">
          <p>Please apply at least one filter to view patient records.</p>
        </div>
      )}
    </div>
  );
};

export default withEuiFiltersProvider(PatientDatabase, { cachePrefix: 'PDB' });
